import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Barcode, ArrowLeft, Store, Zap, ShieldCheck, Send, CheckCircle2, ExternalLink,
  MessageCircle, Mail, KeyRound, Rocket, Info,
} from "lucide-react";

const WHATSAPP_URL = "https://w.app/carlos_soglia";
const SUPPORT_EMAIL = "suporte@magicsearchimagesproducts.com";

export default function ManualBling() {
  return (
    <div className="min-h-screen bg-white text-[#09090B]">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg" data-testid="manual-bling-logo">
            <div className="w-8 h-8 rounded-md brand-primary flex items-center justify-center">
              <Barcode className="w-4 h-4 text-white" />
            </div>
            <span>MAGIC SIP</span>
          </Link>
          <Link to="/">
            <Button variant="outline" className="rounded-md border-zinc-300 h-9" data-testid="manual-bling-back">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar à home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-8 py-10">
        {/* Title */}
        <div className="eyebrow mb-2 flex items-center gap-2">
          <Store className="w-4 h-4 text-[#002FA7]" /> Manual do Integrador
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mb-3">
          Manual de integração · Magic SIP × Bling
        </h1>
        <p className="text-sm text-zinc-500 mb-8 max-w-2xl">
          Passo a passo completo para conectar sua conta Bling ao Magic SIP e começar a publicar
          produtos em lote via API v3 (OAuth 2.0 com refresh token).
        </p>

        {/* Intro */}
        <div className="border border-zinc-200 bg-zinc-50 rounded-md p-5 mb-8 flex items-start gap-3">
          <Info className="w-5 h-5 text-[#002FA7] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-zinc-700">
            <strong className="text-[#09090B]">O que é o Magic SIP?</strong> Um utilitário SaaS que
            cadastra até 10.000 produtos automaticamente no seu Bling a partir de uma planilha simples
            (nome + categoria + preço). A IA busca imagens em catálogos brasileiros, gera descrição SEO,
            SKU e publica em lote — o que levaria dias fazendo manualmente vira minutos.
          </div>
        </div>

        {/* ===== PASSOS NO BLING ===== */}
        <section className="mb-12">
          <h2 className="font-display text-2xl font-bold mb-2 flex items-center gap-2">
            <span className="w-7 h-7 rounded-md brand-primary text-white flex items-center justify-center text-sm font-bold">
              1
            </span>
            Passos no Bling
          </h2>
          <p className="text-sm text-zinc-500 mb-5">
            Para integrar com o Magic SIP na API v3, acesse a sua conta Bling e siga:
          </p>

          <div className="space-y-4">
            <Step
              n={1}
              title="Acesse a Central de Extensões"
              body={
                <>
                  Faça login na sua conta Bling e clique no menu{" "}
                  <a
                    href="https://bling.com.br/central.extensoes.php"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#002FA7] underline"
                  >
                    Central de Extensões
                  </a>
                  .
                </>
              }
            />
            <Step
              n={2}
              title="Busque o aplicativo Magic SIP"
              body={
                <>
                  Use a barra de pesquisa da Central de Extensões e digite <strong>Magic SIP</strong>.
                  O aplicativo será listado.
                </>
              }
            />
            <Step
              n={3}
              title="Clique em Instalar aplicativo"
              body="Ao localizar o Magic SIP, clique no botão Instalar aplicativo para iniciar a autorização OAuth."
            />
            <Step
              n={4}
              title="Confirme a autorização"
              body={
                <>
                  Uma tela do Bling solicitará confirmação. Revise os escopos requisitados
                  (Produtos: leitura/escrita/exclusão) e clique em <strong>Autorizar</strong>. A partir
                  desse momento todas as configurações no Bling estão finalizadas — o Bling
                  redireciona automaticamente de volta para o Magic SIP.
                </>
              }
            />
          </div>
        </section>

        {/* ===== PASSOS NO MAGIC SIP ===== */}
        <section className="mb-12">
          <h2 className="font-display text-2xl font-bold mb-2 flex items-center gap-2">
            <span className="w-7 h-7 rounded-md brand-primary text-white flex items-center justify-center text-sm font-bold">
              2
            </span>
            Passos no Magic SIP (Integrador)
          </h2>

          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-5 mb-5">
            <div className="font-display font-semibold text-[#09090B] mb-2 flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-[#002FA7]" /> Requisitos
            </div>
            <div className="space-y-4 text-sm text-zinc-700">
              <div>
                <div className="font-semibold text-[#09090B] mb-1">
                  Existe a obrigatoriedade de ter uma conta Magic SIP antes de instalar o aplicativo?
                </div>
                <p>
                  <strong>Sim.</strong> Antes de autorizar a integração no Bling, você precisa de uma
                  conta ativa no Magic SIP:
                </p>
                <ol className="list-decimal ml-5 mt-2 space-y-1">
                  <li>
                    Acesse{" "}
                    <a
                      href="https://magicsearchimagesproducts.com/register"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#002FA7] underline"
                    >
                      magicsearchimagesproducts.com/register
                    </a>
                  </li>
                  <li>Forneça nome, e-mail e senha</li>
                  <li>
                    Ative o trial de 7 dias grátis (cartão de crédito exigido para validação de
                    identidade, mas <strong>nada é cobrado</strong> durante o trial)
                  </li>
                  <li>Confirme o e-mail e faça login</li>
                </ol>
              </div>

              <div>
                <div className="font-semibold text-[#09090B] mb-1">
                  Há preparações necessárias na conta Magic SIP antes de autorizar o Bling?
                </div>
                <p>
                  <strong>Não.</strong> Basta ter a conta criada e o trial (ou uma assinatura) ativo.
                  Não há configurações prévias obrigatórias.
                </p>
              </div>
            </div>
          </div>

          {/* Steps in Magic SIP */}
          <div className="space-y-4">
            <Step
              n={1}
              icon={<Store className="w-4 h-4" />}
              title="Acesse Configurações no Magic SIP"
              body={
                <>
                  Depois de logado, no menu lateral clique em <strong>Configurações</strong>. Na aba{" "}
                  <strong>Bling</strong> você encontrará o botão <strong>Conectar Bling</strong>.
                </>
              }
            />
            <Step
              n={2}
              icon={<ExternalLink className="w-4 h-4" />}
              title="Clique em Conectar Bling"
              body={
                <>
                  O Magic SIP redirecionará você para a tela oficial de autorização do Bling
                  (<code className="bg-zinc-100 px-1 rounded font-mono text-xs">bling.com.br/Api/v3/oauth/authorize</code>).
                  Faça login na sua conta Bling se ainda não estiver logado.
                </>
              }
            />
            <Step
              n={3}
              icon={<CheckCircle2 className="w-4 h-4" />}
              title="Autorize os escopos"
              body={
                <>
                  Revise os escopos exigidos (Produtos: leitura/escrita/exclusão) e clique em{" "}
                  <strong>Autorizar</strong>. O Bling redirecionará de volta para o Magic SIP com um
                  código de autorização. O Magic SIP troca esse código por um{" "}
                  <code className="bg-zinc-100 px-1 rounded font-mono text-xs">access_token</code> +{" "}
                  <code className="bg-zinc-100 px-1 rounded font-mono text-xs">refresh_token</code>{" "}
                  automaticamente.
                </>
              }
            />
            <Step
              n={4}
              icon={<ShieldCheck className="w-4 h-4" />}
              title="Confirmação da conexão"
              body="Você verá uma mensagem de sucesso e o botão 'Conectar Bling' será substituído por 'Bling conectado ✓'. As credenciais são armazenadas criptografadas com Fernet no nosso servidor."
            />
            <Step
              n={5}
              icon={<Send className="w-4 h-4" />}
              title="Comece a publicar produtos"
              body={
                <>
                  Vá em <strong>Publicar → Lote</strong>. Carregue uma planilha CSV/XLSX com nome,
                  categoria e preço dos produtos. Clique em <strong>Preparar</strong> — a IA busca
                  imagens e gera descrições. Selecione <strong>Bling</strong> no dropdown "Publicar em"
                  e clique em <strong>Publicar selecionados</strong>. Os produtos são criados na sua
                  conta Bling com imagens, descrição, SKU, estoque e dimensões.
                </>
              }
            />
          </div>

          <div className="mt-6 border border-emerald-200 bg-emerald-50 rounded-md p-4 flex items-start gap-3">
            <Rocket className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <strong>Renovação automática:</strong> o Magic SIP renova o access_token
              automaticamente usando o refresh_token quando necessário. Você não precisa reconectar
              manualmente.
            </div>
          </div>
        </section>

        {/* ===== Escopos ===== */}
        <section className="mb-12">
          <h2 className="font-display text-2xl font-bold mb-3">Escopos utilizados</h2>
          <div className="border border-zinc-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2">Escopo</th>
                  <th className="text-left px-4 py-2">Finalidade</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                <tr>
                  <td className="px-4 py-3 font-mono text-xs">Produtos — Ler</td>
                  <td className="px-4 py-3 text-zinc-700">Consultar produtos já cadastrados para detectar duplicatas.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-xs">Produtos — Incluir</td>
                  <td className="px-4 py-3 text-zinc-700">Criar produtos em lote com dados gerados pela IA.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-xs">Produtos — Alterar</td>
                  <td className="px-4 py-3 text-zinc-700">Anexar imagens externas via PATCH após criação.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-xs">Produtos — Excluir</td>
                  <td className="px-4 py-3 text-zinc-700">Rollback: apagar produtos publicados em massa se o usuário desejar desfazer.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* ===== Contato ===== */}
        <section>
          <h2 className="font-display text-2xl font-bold mb-3">Suporte e Contato</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="border-2 border-emerald-200 bg-emerald-50 rounded-md p-4 flex items-start gap-3 hover:border-emerald-500 transition-colors"
              data-testid="manual-bling-whatsapp"
            >
              <div className="w-10 h-10 rounded-md bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display font-semibold text-sm text-[#09090B]">WhatsApp</div>
                <div className="text-xs text-zinc-600">Resposta rápida, Seg–Sex 9h–18h (BRT)</div>
                <div className="text-xs font-mono text-emerald-800 mt-1">w.app/carlos_soglia</div>
              </div>
            </a>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="border-2 border-zinc-200 rounded-md p-4 flex items-start gap-3 hover:border-[#002FA7] transition-colors"
              data-testid="manual-bling-email"
            >
              <div className="w-10 h-10 rounded-md brand-primary text-white flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display font-semibold text-sm text-[#09090B]">E-mail</div>
                <div className="text-xs text-zinc-600">Para prints, logs e anexos</div>
                <div className="text-xs font-mono text-zinc-700 mt-1">{SUPPORT_EMAIL}</div>
              </div>
            </a>
          </div>
        </section>

        <div className="mt-10 text-xs text-zinc-400 font-mono border-t border-zinc-200 pt-4 flex justify-between flex-wrap gap-2">
          <span>Última atualização: 21 de fevereiro de 2026</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-[#002FA7]">Privacidade</Link>
            <Link to="/support" className="hover:text-[#002FA7]">Suporte</Link>
            <Link to="/pricing" className="hover:text-[#002FA7]">Planos</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function Step({ n, title, body, icon }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-md p-4 flex items-start gap-3">
      <div className="w-8 h-8 rounded-md bg-zinc-100 text-zinc-700 flex items-center justify-center flex-shrink-0 font-mono text-sm font-bold">
        {icon || n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm text-[#09090B] mb-1">{title}</div>
        <div className="text-sm text-zinc-700 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
